/**
 * Helpers for creating Customer Files instance
 */

import type { IBaseCustomer } from "../../BaseCustomerFactory";
import type { File } from "le-coffre-resources/dist/Customer";
import type { IRef } from "@Front/Api/BaseApiService";
import type { QueryPayload } from "@Front/Api/types";
import type { IGetFilesparams, IPutFilesParams } from "../Files";
import {
	createGetMethod,
	createPostMethod,
	createGetUploadLinkMethod,
	createGetDownloadLinkMethod,
	createGetByUidMethod,
	createPutMethod,
	createDeleteMethod,
	createDownloadMethod,
	createDownloadMultipleMethod,
	createDownloadAggregatedCertificateMethod,
	createReportDepositAttemptMethod,
} from "./filesMethodsHelpers";
import type { DepositAttemptPayload } from "@Front/Components/DesignSystem/DepositOtherDocument/helpers/depositAttemptReporter";

export interface FilesInstance {
	get(q: IGetFilesparams): Promise<File[]>;
	post(body: FormData): Promise<File>;
	getUploadLink(uid: string): string;
	getDownloadLink(uid: string, withCertificate?: boolean): string;
	getByUid(uid: string, q?: QueryPayload): Promise<File>;
	put(uid: string, body: IPutFilesParams): Promise<File>;
	delete(uid: string): Promise<File>;
	download(uid: string, withCertificate?: boolean, ref?: IRef): Promise<Blob>;
	downloadMultiple(fileUids: string[], includeAnchors?: boolean, folderNumber?: string, folderName?: string): Promise<Blob>;
	downloadAggregatedCertificate(documentUids: string[], folderUid: string): Promise<Blob>;
	reportDepositAttempt(payload: DepositAttemptPayload): Promise<void>;
}

/**
 * Create Files instance with all methods
 */
export function createFilesInstance(baseCustomer: IBaseCustomer): FilesInstance {
	const baseUrl = baseCustomer.namespaceUrl.concat("/files");

	return {
		get: createGetMethod(baseCustomer, baseUrl),
		post: createPostMethod(baseCustomer, baseUrl),
		getUploadLink: createGetUploadLinkMethod(baseUrl),
		getDownloadLink: createGetDownloadLinkMethod(baseUrl),
		getByUid: createGetByUidMethod(baseCustomer, baseUrl),
		put: createPutMethod(baseCustomer, baseUrl),
		delete: createDeleteMethod(baseCustomer, baseUrl),
		download: createDownloadMethod(baseCustomer, baseUrl),
		downloadMultiple: createDownloadMultipleMethod(baseCustomer, baseUrl),
		downloadAggregatedCertificate: createDownloadAggregatedCertificateMethod(baseCustomer, baseUrl),
		reportDepositAttempt: createReportDepositAttemptMethod(baseUrl),
	};
}
